'use client'

import * as React from 'react'
import Link, { LinkProps } from 'next/link'
import { useRouter } from 'next/navigation'

// Components
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
  DrawerTitle,
} from '@/components/ui/drawer'

// Config
import { navConfig } from '@/config/nav'
import type { Locale } from '@/i18n-config'

// Utils
import { cn } from '@/lib/utils'
import { useMetaColor } from '@/hooks/use-meta-color'

// Updated MobileNav to accept "lang" and render links dynamically like main-nav.
export function MobileNav({ lang }: { lang: Locale }) {
  const [open, setOpen] = React.useState(false)
  const { setMetaColor, metaColor } = useMetaColor()

  const onOpenChange = React.useCallback(
    (open: boolean) => {
      setOpen(open)
      setMetaColor(open ? '#09090b' : metaColor)
    },
    [setMetaColor, metaColor]
  )

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerTrigger asChild>
        <Button variant="ghost" className="size-9 md:hidden ">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="!size-9"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 9h16.5m-16.5 6.75h16.5"
            />
          </svg>
          <span className="sr-only">Toggle Menu</span>
        </Button>
      </DrawerTrigger>
      <DrawerContent className="max-h-[60svh] p-0">
        <DrawerTitle className="sr-only">Mobile navbar</DrawerTitle>
        <div className="overflow-auto p-6">
          <div className="flex flex-col space-y-4">
            {Object.entries(navConfig[lang] || {}).map(([key, item]) => {
              if ('subPaths' in item) {
                return (
                  <div key={key} className="relative flex flex-col space-y-2">
                    <h4 className="font-medium ">{item.title}</h4>
                    <span className="absolute left-0 top-5 h-[74%] w-px bg-muted-foreground" />
                    {item.subPaths.map((sub, idx) => (
                      <div key={idx} className="flex items-center">
                        <span className="h-px w-[15px] bg-muted-foreground" />
                        <MobileLink
                          href={sub.href}
                          onOpenChange={setOpen}
                          className="pl-1"
                        >
                          {sub.title}
                        </MobileLink>
                      </div>
                    ))}
                  </div>
                )
              }
              return (
                <MobileLink key={key} href={item.href} onOpenChange={setOpen}>
                  {item.title}
                </MobileLink>
              )
            })}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}

interface MobileLinkProps extends LinkProps {
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
  className?: string
}

function MobileLink({
  href,
  onOpenChange,
  className,
  children,
  ...props
}: MobileLinkProps) {
  const router = useRouter()
  return (
    <Link
      href={href}
      onClick={() => {
        router.push(href.toString())
        onOpenChange?.(false)
      }}
      className={cn('text-base', className)}
      {...props}
    >
      {children}
    </Link>
  )
}
